/**
 * Tenant guards
 *
 * - tenantAvailabilityGuard (beforeEach): blocks navigation to tenants the
 *   current user has no access to, redirecting to notFound.
 * - tenantSelectionGuard (beforeResolve): syncs the tenantId route param into
 *   the permissions store once the navigation is confirmed.
 *
 * Tenant identity: route params carry the tenant in `id` form (dash, e.g.
 * "gls-de") — the same form used in URLs and REST paths. `hasTenantAccess`
 * normalizes internally and also tolerates the underscore scope-path form
 * ("gls_de"), so an underscore tenant id passing this guard is BY DESIGN.
 * See the tenant identity model in src/stores/permissions.ts.
 */

import type { NavigationGuard, RouteLocationNormalized } from 'vue-router';

import { logAppError } from '@/config/sentryConfig';
import { usePermissionsStore } from '@/stores/permissions';
import { isUserAuthenticated } from './helpers';

// Route params are string | string[] (repeatable params). Tenant routes only
// declare a single :tenantId segment, so take the first value defensively.
const getTenantIdParam = (
  route: RouteLocationNormalized
): string | undefined => {
  const { tenantId } = route.params;
  return Array.isArray(tenantId) ? tenantId[0] : tenantId;
};

const isTenantRoute = (route: RouteLocationNormalized): boolean => {
  return route.path.startsWith('/tenant') && Boolean(getTenantIdParam(route));
};

const checkTenantAvailability = (tenantId: string): boolean => {
  const permissionsStore = usePermissionsStore();
  return permissionsStore.hasTenantAccess(tenantId);
};

export const tenantAvailabilityGuard: NavigationGuard = async (
  to,
  from,
  next
) => {
  // Skip tenant checks if user isn't authenticated yet
  if (!isUserAuthenticated()) {
    return next();
  }

  if (isTenantRoute(to)) {
    const tenantId = getTenantIdParam(to);
    if (tenantId && checkTenantAvailability(tenantId)) {
      return next();
    } else {
      // Tenant not available for this user — redirects to notFound, which is
      // otherwise invisible to Sentry, so report it at warning level.
      logAppError({
        error: new Error('Tenant not available for current user'),
        functionName: 'tenantAvailabilityGuard',
        section: 'router',
        level: 'warning',
        metadata: { tenantId, path: to.fullPath }
      });
      return next({ name: 'notFound' });
    }
  }

  next();
};

export const tenantSelectionGuard: NavigationGuard = async (to, from, next) => {
  // Skip tenant selection if user isn't authenticated yet
  if (!isUserAuthenticated()) {
    return next();
  }

  if (isTenantRoute(to)) {
    const tenantId = getTenantIdParam(to);
    if (tenantId) {
      // Set selected tenant in permissions store
      const permissionsStore = usePermissionsStore();
      permissionsStore.selectedTenant = tenantId;
    }
  }

  next();
};
